import { getApiClient } from '@/lib/api/api-client';
import type { Nftcollection } from '@/types/nftcollection';
import type { BaseQueryParams, PaginatedResponse, FindNftCollectionsParams, CatalogItem, WhereFilter } from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { nftCollectionTransformer, EnrichedCollection } from '@/lib/transformers/nftcollection.transformer';
import { CACHE_DURATION, DEFAULT_PAGINATION, STATUS_VALUES } from './catalog-constants';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
import { logger } from '@/lib/utils/logger';
import { LEAN_COLLECTION, LEAN_POPULATE } from './lean-fields';
import {
  NftCollectionSchema,
  safeParseWithLogging,
  createPaginatedValidator
} from '@/lib/validations/catalog';

// Re-export for backwards compatibility
export type Collection = EnrichedCollection;

export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : DEFAULT_PAGINATION.LIMIT,
    query: params.get('query') || undefined,
    category: params.get('category') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

export function nftCollectionToCatalogItemSync(nftCollection: Nftcollection): CatalogItem {
  return nftCollectionTransformer.toCatalogItem(nftCollection);
}

export async function nftCollectionToCatalogItem(nftCollection: Nftcollection): Promise<CatalogItem> {
  const apiClient = getApiClient();
  let artworkCount = null;
  let subCollectorCount = 0;

  try {
    let subArtworksCount: number = 0;
    if (nftCollection.parentCollection == null) {
      const subs = await NftCollectionsAPI.apiClient.collections.nftcollections.find({
        limit: 100,
        select: {
          name: true,
          uniqueHolders: true,
        },
        where: {
          parentCollection: {
            equals: nftCollection.id,
          },
        },
      });

      if (subs.docs.length > 0) {
        for (const collection of subs.docs) {
          const artworksCountResult = await apiClient.collections.artworks.count({
            where: {
              collection: {
                equals: collection.id,
              },
            },
          });
          subArtworksCount += artworksCountResult.totalDocs || 0;

          if (collection.uniqueHolders) {
            subCollectorCount += collection.uniqueHolders;
          }
        }
      }
    }

    if (typeof window === 'undefined') {
      const artworksCountResult = await apiClient.collections.artworks.count({
        where: {
          collection: {
            equals: nftCollection.id,
          },
        },
      });
      const mainCollectionArtworkCount = artworksCountResult.totalDocs || 0;
      artworkCount = mainCollectionArtworkCount;

      if (subArtworksCount > 0) {
        artworkCount += subArtworksCount;
      }
    }
  } catch (error) {
    logger.error('Error counting artworks:', error);
  }

  const catalogItem = nftCollectionTransformer
    .setAdditionalData({
      artworkCount: artworkCount || nftCollection.totalSupply || 0,
      collectorCount: (nftCollection.uniqueHolders || 0) + subCollectorCount,
    })
    .toCatalogItem(nftCollection);

  return catalogItem;
}

export class NftCollectionsAPI {
  static apiClient = getApiClient();

  static async findNftCollections(
    params: FindNftCollectionsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const {
      page = DEFAULT_PAGINATION.PAGE,
      limit = DEFAULT_PAGINATION.LIMIT,
      query,
      category,
      sort,
      years,
      medium,
      platform,
      chains,
      type,
      getChildCollections = false,
      parentid,
      ...rest
    } = params;

    // NFT collections use 'name' for title, 'createdDate' for date
    const sortValue = getSortValue(sort, { dateField: 'createdDate', titleField: 'name' });
    // Prepend status and featured for NFT collection sorting
    const sorts = ['-status', 'featured', sortValue, 'id'] as const;

    const where: WhereFilter = {};

    where.status = {
      not_equals: STATUS_VALUES.DRAFT,
    };

    where.hidelisting = {
      not_equals: true,
    };

    if (getChildCollections) {
      where.parentCollection = {
        equals: parentid,
      };
    } else {
      where.parentCollection = {
        exists: false,
      };
    }

    if (query) {
      where.name = {
        contains: query,
      };
    }

    if (category) {
      where.category = {
        equals: category,
      };
    }

    if (years && years.length > 0) {
      try {
        const yearFilters = years.map(year => {
          const startDate = new Date(parseInt(year), 0, 1, 0, 0, 0, 0).toISOString();
          const endDate = new Date(parseInt(year), 11, 31, 23, 59, 59, 999).toISOString();
          return {
            startDate: {
              between: [startDate, endDate],
            },
          };
        });

        // Always use array for or clause
        where.or = yearFilters as WhereFilter[];
      } catch (error) {
        logger.error('Error processing years:', error);
      }
    }

    if (medium && medium.length > 0) {
      where.artCategory = {
        in: medium,
      };
    }

    if (platform && platform.length > 0) {
      where['requiredInformation.datamarket'] = {
        in: platform,
      };
    }

    if (chains && chains.length > 0) {
      where['requiredInformation.blockchain'] = {
        in: chains,
      };
    }

    // Additional where parameters from rest (properly typed)
    const additionalWhereParams = rest as Record<string, unknown>;
    Object.assign(where, additionalWhereParams);

    try {
      // Valid Nftcollection sort fields
      type NftcollectionSortField = keyof Nftcollection | `-${keyof Nftcollection}` | 'featured' | '-featured' | '-status';

      const result = await NftCollectionsAPI.apiClient.collections.nftcollections.find({
        limit,
        page,
        sort: sorts.join(',') as NftcollectionSortField,
        where,
        depth: 2, // Expand related fields (cardImage, bannerImage, etc.)
        select: LEAN_COLLECTION,
        populate: LEAN_POPULATE,
      });

      const response = result as PaginatedResponse<Nftcollection>;

      // Validate the paginated response
      if (response.docs && response.docs.length > 0) {
        const PaginatedNftCollectionSchema = createPaginatedValidator(NftCollectionSchema);
        const validationResult = safeParseWithLogging(
          PaginatedNftCollectionSchema,
          response,
          'NftCollectionService.findNftCollections'
        );
        if (!validationResult.success && 'error' in validationResult) {
          logger.warn('Paginated NFT collections validation failed, using raw response:', validationResult.error);
          // Continue with raw response for backward compatibility
        }
      }

      if ('totalPages' in response && response.totalPages && 'page' in response && response.page) {
        response.hasNextPage = response.page < response.totalPages;

        const catalogItems = await Promise.all(
          response.docs.map((collection: Nftcollection) => nftCollectionToCatalogItem(collection))
        );

        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        result.hasNextPage = false;
        return success({
          ...result,
          docs: [],
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findNftCollections', error);
    }
  }

  static async getBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Nftcollection>> {
    const cacheKey = await CacheService.scopedKey(`nftcollection.service_${slug}`);

    if (!skipCache) {
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Collection);
      }
    }

    try {
      const response = await NftCollectionsAPI.apiClient.collections.nftcollections.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2, // Expand related fields (cardImage, bannerImage, layout media, etc.)
        select: LEAN_COLLECTION,
        populate: LEAN_POPULATE,
      });

      if (response.docs[0]) {
        // Validate the response with Zod
        const validationResult = safeParseWithLogging(
          NftCollectionSchema,
          response.docs[0],
          'NftCollectionService.getBySlug'
        );
        if (!validationResult.success && 'error' in validationResult) {
          logger.warn('NFT Collection validation failed, using raw response:', validationResult.error);
          // Continue with raw response for backward compatibility
        }

        // Enrich collection with computed display fields (tags, categories, drop status)
        const nftCollection = nftCollectionTransformer.enrichCollection(response.docs[0]);
        CacheService.addToCache(cacheKey, nftCollection, CACHE_DURATION);
        return success(nftCollection);
      }

      return success(null);
    } catch (error) {
      return handleApiError('getBySlug', error);
    }
  }
}
